import React, { useCallback, useEffect, useState } from "react";
import { set, lensPath, not, pick } from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import { useGenerateRipEndpoint } from "../cspServices";
import Divider from "@material-ui/core/Divider";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import {
  validateRipEndpoint,
  validateRipEndpointField,
} from "../cspProjectValidation";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const RipEndpointDialog = ({ project, open, onCancel, onApply }) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    ripEndpoint: null,
    ripEndpointId: null,
  });

  useEffect(() => {
    if (notBlank(project)) {
      setFields(pick(["ripEndpoint", "ripEndpointId"], project));
    }
  }, [project]);

  const {
    addValidationError,
    removeValidationError,
    removeValidationErrors,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const [generating, setGenerating] = useState(false);
  const [creatorOrgId, setCreatorOrgId] = useState(null);

  const generateRipEndpoint = useGenerateRipEndpoint();
  const handleGenerateClicked = useCallback(async () => {
    if (notBlank(creatorOrgId)) {
      setGenerating(true);
      try {
        const ripData = await generateRipEndpoint.mutateAsync({
          resourceIds: {
            programKey: project.program.programKey,
            projectKey: project.projectKey,
            creatorOrganizationId: creatorOrgId,
          },
          data: {},
        });
        setFields({
          ripEndpoint: ripData.ripEndpoint,
          ripEndpointId: ripData.ripEndpointId,
        });
        setCreatorOrgId("");
        removeValidationErrors();
      } catch (err) {
        setGeneralError(err);
      } finally {
        setGenerating(false);
      }
    } else {
      addValidationError({
        creatorOrgId: ["A Creator Organization Id must be entered."],
      });
    }
  }, [
    creatorOrgId,
    generateRipEndpoint,
    project.projectKey,
    project.program.programKey,
    addValidationError,
    removeValidationErrors,
    setGeneralError,
  ]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleBlur = (field, value) => {
    const errors = validateRipEndpointField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleApply = async () => {
    const validationErrors = validateRipEndpoint(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      removeValidationErrors();
      onApply(fields);
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">Edit RIP Endpoint</DialogTitle>
      <DialogContent>
        <DialogErrors />
        <DialogContentText>
          Enter RIP Endpoint details or generate a RIP Endpoint for a Creator
          Organization.
        </DialogContentText>
        {generating && <PanelLoadingIndicator />}
        {not(generating) && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="rip-endpoint"
                fullWidth
                label="RIP Endpoint"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.ripEndpoint}
                onChange={handleTextChange(["ripEndpoint"])}
                error={hasValidationError("ripEndpoint")}
                helperText={firstValidationError("ripEndpoint")}
                onBlur={() => handleBlur("ripEndpoint", fields.ripEndpoint)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="rip-endpoint-id"
                fullWidth
                label="RIP Endpoint Id"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.ripEndpointId}
                onChange={handleTextChange(["ripEndpointId"])}
                error={hasValidationError("ripEndpointId")}
                helperText={firstValidationError("ripEndpointId")}
                onBlur={() => handleBlur("ripEndpointId", fields.ripEndpointId)}
              />
            </Grid>
          </Grid>
        )}

        <Divider style={{ marginTop: 30, marginBottom: 30 }} />
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={10}>
            <TextField
              id="creator-org-id"
              fullWidth
              label="Creator Organization Id"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={creatorOrgId}
              onChange={event => setCreatorOrgId(event.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="outlined"
              onClick={handleGenerateClicked}
              disabled={isBlank(creatorOrgId) || generating}
              aria-label="add option"
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} disabled={generating} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RipEndpointDialog;
